import { Drawer as AntDrawer, type DrawerProps as AntDrawerProps } from "antd/es";
import isFunction from "lodash/isFunction";
import React from "react";

import { ZIndexBoundary } from "~/components/z-index-boundary";

import { DrawerContext, type DrawerContextValue } from "./_drawer-context";

const AntDrawerWithChildren: React.FC<React.PropsWithChildren<AntDrawerProps>> = AntDrawer;

export type DrawerPlacement = AntDrawerProps["placement"];

export type DrawerNode<TContent> =
	| React.ReactNode
	| ((content: TContent, close: VoidFunction, preservedContent?: TContent) => React.ReactNode);

export interface DrawerProps<TContent = boolean> {
	when: undefined | null | false | TContent;

	preservedContent?: null | false | TContent;

	/**
	 * @param dismissed `true` when the user dimisses the Drawer via ESC key or clicking away, `false` when the context's `close` function is called imperatively
	 * @param state Defined when dismissed is `true`, undefined when dismissed is `false`
	 * @returns void
	 */
	onClose: (dismissed: boolean, state: undefined | TContent) => void;

	placement: DrawerPlacement;
	bodyStyle?: React.CSSProperties;
	style?: React.CSSProperties;
	width?: string | number;
	zIndex?: number;
	push?: AntDrawerProps["push"];
	children: DrawerNode<TContent>;
}

enum ClosedBy {
	DISMISSAL = 0,
	INTERACTION = 1,
}

export const Drawer = <TContent,>({
	when,
	preservedContent,
	children,
	onClose,
	bodyStyle,
	...props
}: DrawerProps<TContent>): React.ReactElement => {
	const [closingBy, setClosingBy] = React.useState<ClosedBy>();
	const context = React.useMemo<DrawerContextValue>(
		() => ({
			close: () => setClosingBy(ClosedBy.INTERACTION),
		}),
		[],
	);

	return (
		<DrawerContext.Provider value={context}>
			<ZIndexBoundary asChild zIndex={100}>
				<AntDrawerWithChildren
					destroyOnClose
					{...props}
					afterVisibleChange={(visible) => {
						if (!visible && closingBy != null) {
							onClose(closingBy === ClosedBy.DISMISSAL, (preservedContent ?? when) || undefined);

							setClosingBy(undefined);
						}
					}}
					bodyStyle={{ padding: 0, ...bodyStyle }}
					closable={false}
					onClose={() => {
						setClosingBy(ClosedBy.DISMISSAL);
					}}
					visible={when != null && when !== false && closingBy == null}
				>
					{isFunction(children)
						? when == null || when === false
							? null
							: children(when, context.close, preservedContent || undefined)
						: children}
				</AntDrawerWithChildren>
			</ZIndexBoundary>
		</DrawerContext.Provider>
	);
};
